import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  faArrowDownShortWide,
  faArrowDownWideShort,
  faFilter,
  faRoute,
  faTrash,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import SkeletonLoader from 'tiny-skeleton-loader-react';
import useGetRequest from '../../api/useGetRequest';
import useGetTourPlanningDetails from '../../api/tours-planning/useGetTourPlanningDetails';
import usePostScheduleTours from '../../api/tours-planning/usePostScheduleTours';
import useDeleteTourPlanning from '../../api/tours-planning/useDeleteTourPlanning';
import GenericPageContainer from '../../components/agv-generic-page-container';
import Datatable from '../../components/agv-datatable';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import AgrivitechPagination from '../../components/agv-pagination';
import MainButton from '../../components/agv-button';
import GenericModal from '../../components/agv-generic-modal';
import Status from '../../components/agv-status';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import { ORDER_BY_ENUM } from '../../enums/orderby.enum';
import { buildUrlWithParams } from '../../utils/url.utils';
import { useSelector } from 'react-redux';
import './OrdersPool.css';

moment.locale('fr');

export default function OrdersPool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [shouldFetchIds, setShouldFetchIds] = useState(false);
  const [idsHasBeenFetched, setIdsHasBeenFetched] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const features = useSelector((state) => state.features);

  const [filters, setFilters] = useState({
    limit: 24,
    offset: searchParams.get('offset') || 0,
    query: searchParams.get('query') || '',
    order:
      searchParams.get('order') === ORDER_BY_ENUM.ASC ||
      searchParams.get('order') === ORDER_BY_ENUM.DESC
        ? searchParams.get('order')
        : ORDER_BY_ENUM.DESC,
    tour_date: searchParams.get('tour_date') || '',
    client_name: searchParams.get('client_name') || '',
  });

  /**
   * Handle the update of filters when a filter is changed
   * @param {string} key - The key of the filter
   * @param {string} value - The value of the filter
   * @returns {void}
   */
  const handleUpdateParam = useCallback(
    (key, value) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters, [key]: value };

        if (key !== 'offset') {
          newFilters.offset = 0;
        }

        if (key === 'tour_date') {
          if (value) {
            newFilters[key] = value;
          } else {
            delete newFilters[key];
          }
        }

        if (
          (key === 'client_id' || key === 'client_name') &&
          (value === null || value === undefined || value === '')
        ) {
          delete newFilters[key];
        }

        return newFilters;
      });
    },
    [navigate]
  );

  const url = useMemo(
    () => buildUrlWithParams('/tours-planning', filters),
    [filters]
  );

  const idsUrl = useMemo(
    () =>
      shouldFetchIds
        ? buildUrlWithParams('/tours-planning/ids', {
            query: filters.query,
            client_name: filters.client_name,
            tour_date: filters.tour_date,
            order: filters.order,
          })
        : null,
    [
      shouldFetchIds,
      filters.query,
      filters.client_name,
      filters.tour_date,
      filters.order,
    ]
  );

  const { data, isLoading, isFirstLoad, mutate } = useGetRequest(url, 300);

  const { data: filtersData, isLoading: isLoadingFilters } = useGetRequest(
    '/tours-planning/filters',
    300
  );

  const { data: idsData, isLoading: isLoadingIds } = useGetRequest(idsUrl, 0);

  const {
    isLoading: isSchedulingLoading,
    isSuccess: isSchedulingSuccess,
    scheduleTours,
  } = usePostScheduleTours();

  const { deleteTourPlanning, isDeleting } = useDeleteTourPlanning();

  const {
    isLoading: isDetailsLoading,
    data: detailsData,
    fetchDetails,
    reset: resetDetails,
  } = useGetTourPlanningDetails();

  /**
   * Update the URL with the new filters
   * @returns {void}
   */
  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  /**
   * Handle IDs data when it arrives
   */
  useEffect(() => {
    if (idsData && shouldFetchIds) {
      try {
        const { ids } = idsData;
        setSelectedItems(new Set(ids));
        toast.success(`${ids.length} étape(s) sélectionnée(s) au total`);
        setIdsHasBeenFetched(true);
      } catch (error) {
        console.error('Erreur lors de la récupération des IDs:', error);
        toast.error('Erreur lors de la sélection de toutes les étapes');
        setIdsHasBeenFetched(false);
      } finally {
        setIsSelectingAll(false);
        setShouldFetchIds(false);
      }
    }
  }, [idsData, shouldFetchIds]);

  /**
   * Calculate total weight and items count from delivery items
   * @param {array} items - Array of delivery items
   * @returns {object} - Object with total weight and items count
   */
  const calculateTotals = (items) => {
    if (!items || items.length === 0) return { totalWeight: 0, itemsCount: 0 };

    const totalWeight = items.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return sum + weight * quantity;
    }, 0);

    return {
      totalWeight: totalWeight.toFixed(2),
      itemsCount: items.length,
    };
  };

  /**
   * Get planning status
   * @param {object} element - The planning element
   * @returns {object} - Status object with enum and text
   */
  const getPlanningStatus = (element) => {
    switch (element?.status) {
      case 'PLANNED':
        return { status: STATUS_ENUM.WARN, text: 'Planifié' };
      case 'CREATED':
        return { status: STATUS_ENUM.NEUTRAL, text: 'Créé' };
      case 'IN_PROGRESS':
        return { status: STATUS_ENUM.POSITIVE, text: 'En cours' };
      case 'COMPLETED':
        return { status: STATUS_ENUM.POSITIVE, text: 'Terminé' };
      case 'CANCELLED':
        return { status: STATUS_ENUM.NEGATIVE, text: 'Annulé' };
      default:
        return { status: STATUS_ENUM.NEUTRAL, text: 'Non défini' };
    }
  };

  /**
   * Handle item selection
   * @param {string} itemId - The item ID to toggle
   */
  const handleItemSelection = (itemId) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
    setIdsHasBeenFetched(false);
  };

  /**
   * Handle select all visible items
   */
  const handleSelectAllVisible = () => {
    const items = data?.items || [];
    if (selectedItems.size === items.length && items.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((item) => item.id)));
    }
  };

  /**
   * Handle select all results (with API call to get all item IDs)
   */
  const handleSelectAllResults = () => {
    setIsSelectingAll(true);
    setShouldFetchIds(true);
  };

  /**
   * Check if all visible items are selected
   */
  const areAllVisibleSelected = () => {
    const items = data?.items || [];
    return items.length > 0 && selectedItems.size === items.length;
  };

  const needToShowSelectAllOption = () => {
    const items = data?.items || [];
    return (
      items.length > 0 &&
      selectedItems.size === items.length &&
      (selectedItems.size >= filters.limit || idsHasBeenFetched)
    );
  };

  /**
   * Get the appropriate message for "select all" based on current filters
   */
  const getSelectAllMessage = () => {
    const simulatedTotal = data?.pagination_info?.total_items;
    const hasFilters =
      filters.query || filters.client_name || filters.tour_date;

    if (hasFilters) {
      return `Sélectionner tous les ${simulatedTotal} résultats correspondant à cette recherche`;
    } else {
      return `Sélectionner toutes les ${simulatedTotal} étapes dans le puits de commande`;
    }
  };

  const onPlanningClick = (element) => {
    if (!element?.id) {
      toast.error('Impossible de charger les détails de cette commande.');
      return;
    }
    setIsDetailsModalOpen(true);
    fetchDetails(element.id);
  };

  /**
   * Handle opening the date modal for scheduling
   */
  const handleOpenDateModal = () => {
    if (selectedItems.size === 0) return;
    setIsDateModalOpen(true);
  };

  /**
   * Handle sending selected steps to scheduling with selected date
   */
  const handleConfirmScheduling = async () => {
    if (selectedItems.size === 0 || !selectedDeliveryDate) return;
    const selectedIds = Array.from(selectedItems);
    const formattedDate = moment(selectedDeliveryDate).format('YYYY-MM-DD');
    await scheduleTours(selectedIds, formattedDate);
    setIsDateModalOpen(false);
    setSelectedDeliveryDate(null);
  };

  useEffect(() => {
    if (isSchedulingSuccess) {
      setSelectedItems(new Set());
      mutate();
    }
  }, [isSchedulingSuccess]);

  const handleDeleteItem = async (tourPlanningId, element) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer l'étape ${element?.raw_data?.step?.end_address?.name || element?.data_key || tourPlanningId} ?`
      )
    ) {
      return;
    }

    try {
      await deleteTourPlanning({ tourPlanningId });

      toast.success(
        `Étape ${element?.raw_data?.step?.end_address?.name || element?.data_key || tourPlanningId} supprimée avec succès`
      );

      if (selectedItems.has(tourPlanningId)) {
        const newSelectedItems = new Set(selectedItems);
        newSelectedItems.delete(tourPlanningId);
        setSelectedItems(newSelectedItems);
      }

      mutate();
    } catch (error) {
      toast.error("Erreur lors de la suppression de l'étape");
    }
  };

  return (
    <GenericPageContainer
      title={'Planification des tournées'}
      subtitle={'Gérez et organisez toutes les livraisons planifiées'}
      filters={
        <>
          <SearchFilter
            value={filters.query}
            setValue={(value) => handleUpdateParam('query', value)}
            placeholder="Rechercher par client, adresse, commentaire..."
          />
          <div className="tw-relative">
            <ReactDatePicker
              selected={filters.tour_date ? new Date(filters.tour_date) : null}
              onChange={(date) =>
                handleUpdateParam(
                  'tour_date',
                  date ? moment(date).format('YYYY-MM-DD') : ''
                )
              }
              customInput={<CustomDatePickerDashboard />}
              locale="fr"
              dateFormat="dd MMM yyyy"
              isClearable
              className="custom-datepicker"
              placeholderText="Sélectionner une date"
            />
          </div>
          <SelectFilter
            value={filters.client_name}
            onChange={(value) => handleUpdateParam('client_name', value)}
            options={[
              { value: '', name: 'Tous les clients' },
              ...(filtersData?.client_names || []).map((clientName) => ({
                value: clientName,
                name: clientName,
              })),
            ]}
            icon={faFilter}
            disabled={isLoadingFilters}
          />
          <SelectFilter
            value={filters.order}
            onChange={(value) => handleUpdateParam('order', value)}
            options={[
              { value: ORDER_BY_ENUM.DESC, name: 'Plus récent' },
              { value: ORDER_BY_ENUM.ASC, name: 'Plus ancien' },
            ]}
            icon={
              filters.order === ORDER_BY_ENUM.DESC
                ? faArrowDownWideShort
                : faArrowDownShortWide
            }
          />
        </>
      }
    >
      <button
        className={`btn btn-success tw-mr-2 ${selectedItems.size === 0 || isSchedulingLoading ? 'disabled' : ''}`}
        onClick={handleOpenDateModal}
        disabled={selectedItems.size === 0 || isLoading || isSchedulingLoading}
        title="Envoyer les étapes sélectionnées en ordonnancement"
        style={{
          opacity: selectedItems.size === 0 || isSchedulingLoading ? 0.6 : 1,
          cursor:
            selectedItems.size === 0 || isSchedulingLoading
              ? 'not-allowed'
              : 'pointer',
        }}
      >
        <FontAwesomeIcon icon={faRoute} className="me-1" />
        Envoyer en ordonnancement
        {selectedItems.size > 0 && (
          <span className="badge bg-light text-dark ms-2">
            {selectedItems.size} étape{selectedItems.size > 1 ? 's' : ''}
          </span>
        )}
      </button>
      {/* Gmail-style selection message */}
      {needToShowSelectAllOption() && (data?.items?.length || 0) > 0 && (
        <div
          className="selection-info mb-3 p-3 border rounded"
          style={{ backgroundColor: '#e3f2fd', borderColor: '#2196f3' }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <span style={{ color: '#1976d2', fontSize: '0.875rem' }}>
              Les <strong>{data?.items?.length || 0}</strong> étapes de cette
              page sont sélectionnées.
            </span>
            <button
              className="btn btn-link btn-sm p-0 mt-2 mt-md-0"
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                fontSize: '0.875rem',
              }}
              onClick={handleSelectAllResults}
              disabled={isSelectingAll}
            >
              {getSelectAllMessage()}
            </button>
          </div>
        </div>
      )}

      <Datatable
        style={'tw-mt-5'}
        isLoading={isLoading}
        isFirstLoading={isFirstLoad && isLoading}
        header={[
          <div
            key="selection-header"
            className="d-flex justify-content-center align-items-center"
          >
            <input
              className="form-check-input"
              type="checkbox"
              id="selectAllVisibleHeader"
              checked={areAllVisibleSelected()}
              onChange={handleSelectAllVisible}
              style={{ margin: '0' }}
            />
          </div>,
          'ETAPE / CLIENT',
          'VEHICULE',
          'ADRESSE',
          'DATE LIVRAISON',
          'STATUT',
          'ACTIONS',
        ]}
        data={data?.items}
        values={(element) => {
          const planningStatus = getPlanningStatus(element);
          const stepData = element?.raw_data?.step;
          const endAddress = stepData?.end_address;
          const deliveryDetails = element?.delivery_details || [];
          const allItems = deliveryDetails.flatMap((dd) => dd.items || []);
          const totals = calculateTotals(allItems);
          const blsCount = deliveryDetails.length;
          const vehicleImat = stepData?.vehicle_imat_if_any;

          return [
            <div
              key={`${element.id}_selection`}
              className="d-flex justify-content-center align-items-center"
            >
              <input
                className="form-check-input"
                type="checkbox"
                id={`planning-${element.id}`}
                checked={selectedItems.has(element.id)}
                onChange={() => handleItemSelection(element.id)}
                style={{ margin: '0' }}
              />
            </div>,
            <div key={`${element.id}_step_client`}>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                {endAddress?.name || 'Client non défini'}
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                {endAddress?.address_key || element?.data_key}
              </div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {blsCount > 1 && (
                  <Status
                    status={STATUS_ENUM.SECONDARY}
                    text={`${blsCount} BLs`}
                  />
                )}
                {totals.itemsCount > 1 && (
                  <Status
                    status={STATUS_ENUM.NEUTRAL}
                    text={`${totals.itemsCount} articles`}
                  />
                )}
              </div>
            </div>,
            <div key={`${element.id}_vehicle`}>
              <Status
                status={vehicleImat ? STATUS_ENUM.INFO : STATUS_ENUM.NEUTRAL}
                text={vehicleImat || 'Non assigné'}
              />
            </div>,
            <div key={`${element.id}_address`} style={{ maxWidth: '200px' }}>
              {endAddress ? (
                <div
                  className="text-truncate"
                  title={`${endAddress.street}, ${endAddress.city} ${endAddress.zipcode}`}
                >
                  <div className="fw-medium" style={{ fontSize: '0.9rem' }}>
                    {endAddress.street}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {endAddress.city} {endAddress.zipcode}
                  </div>
                </div>
              ) : (
                <span className="text-muted">Non définie</span>
              )}
            </div>,
            <div key={`${element.id}_date`}>
              {element?.delivery_date ? (
                <span className="fw-medium">
                  {new Date(element.delivery_date).toLocaleDateString('fr-FR')}
                </span>
              ) : (
                <span className="text-muted">Non définie</span>
              )}
            </div>,
            <div key={`${element.id}_status`}>
              <Status
                status={planningStatus.status}
                text={planningStatus.text}
              />
            </div>,
            <div
              key={`${element.id}_actions`}
              className="tw-flex tw-gap-2 tw-items-center"
            >
              <MainButton
                onClick={() => onPlanningClick(element)}
                text="Détails"
                variant="secondary"
              />
              {features?.TOUR_PLANNING?.ORDERS_POOL?.canDeleteOrders && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteItem(element.id, element)}
                  title="Supprimer cette étape"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>,
          ];
        }}
      />

      {/* Pagination */}
      <AgrivitechPagination
        data={data}
        setOffset={(value) => handleUpdateParam('offset', value)}
      />

      {/* Details Modal */}
      <GenericModal
        isOpen={isDetailsModalOpen}
        setIsOpen={(open) => {
          setIsDetailsModalOpen(open);
          if (!open) resetDetails();
        }}
        title="Détails de la commande"
        size={MODAL_SIZE_STYLE[MODAL_SIZE.XL]}
        mainAction={() => {
          setIsDetailsModalOpen(false);
          resetDetails();
        }}
        mainActionText="Fermer"
      >
        {/* En-tête — style ModalTour */}
        <div className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-w-full">
          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-text-text-dark-high-importance">
            <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-shrink-0 tw-gap-4">
              <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="tw-text-icon-gray"
                />
              </div>
              <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                {isDetailsLoading ? (
                  <>
                    <SkeletonLoader
                      width={200}
                      height={16}
                      style={{ borderRadius: 4 }}
                    />
                    <SkeletonLoader
                      width={140}
                      height={12}
                      style={{ borderRadius: 4, marginTop: 4 }}
                    />
                  </>
                ) : (
                  <>
                    <div className="tw-relative tw-font-medium tw-font-work tw-text-sm">
                      {detailsData?.address?.name ||
                        detailsData?.data_key ||
                        '—'}
                    </div>
                    <div className="list-item-title tw-text-gray-600">
                      {[
                        detailsData?.address?.address_key,
                        detailsData?.address?.zipcode,
                        detailsData?.address?.city,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-4 tw-shrink-0">
            {isDetailsLoading ? (
              <SkeletonLoader
                width={80}
                height={22}
                style={{ borderRadius: 12 }}
              />
            ) : (
              <Status
                status={getPlanningStatus(detailsData)?.status}
                text={getPlanningStatus(detailsData)?.text}
              />
            )}
          </div>
        </div>
        <div className="tw-h-[1px] tw-w-full tw-bg-black tw-opacity-20 tw-mt-4 tw-mb-2" />

        {/* Corps — grille 2 colonnes */}
        <div className="tw-mt-6 tw-grid tw-grid-cols-2 tw-gap-6">
          {/* Colonne gauche : Destinataire */}
          <div>
            <div className="modal-label-group">
              <div className="modal-label-title">Destinataire</div>
              <div className="modal-label-description">
                Adresse et coordonnées du destinataire
              </div>
            </div>

            {isDetailsLoading ? (
              <div className="tw-mt-4 tw-space-y-3">
                <SkeletonLoader
                  width="100%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
                <SkeletonLoader
                  width="80%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
                <SkeletonLoader
                  width="60%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
                <SkeletonLoader
                  width="70%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
              </div>
            ) : detailsData?.address ? (
              <div className="tw-mt-4 tw-space-y-3">
                {(detailsData.address.street ||
                  detailsData.address.address_number) && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Adresse
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {[
                        detailsData.address.address_number,
                        detailsData.address.street,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </div>
                  </div>
                )}
                {(detailsData.address.zipcode || detailsData.address.city) && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Ville
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {[detailsData.address.zipcode, detailsData.address.city]
                        .filter(Boolean)
                        .join(' ')}
                      {detailsData.address.country &&
                        ` (${detailsData.address.country})`}
                    </div>
                  </div>
                )}
                {detailsData.address.region && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Région
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {detailsData.address.region}
                    </div>
                  </div>
                )}
                {detailsData.address.email && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Email
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {detailsData.address.email}
                    </div>
                  </div>
                )}
                {detailsData.address.communication_preference && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Préférence de contact
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {detailsData.address.communication_preference}
                    </div>
                  </div>
                )}

                {/* Téléphones */}
                {detailsData.address.phone_numbers?.length > 0 && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-1">
                      Téléphones
                    </div>
                    <div className="tw-space-y-1.5">
                      {detailsData.address.phone_numbers.map((phone, idx) => (
                        <div
                          key={idx}
                          className="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-slategray-default tw-rounded tw-border tw-border-solid tw-border-background"
                        >
                          <span className="tw-text-smi tw-font-medium tw-font-work">
                            {phone.phone_number}
                          </span>
                          {phone.name && (
                            <span className="tw-text-smi tw-text-text-dark-medium-importance">
                              · {phone.name}
                            </span>
                          )}
                          {phone.fullname && (
                            <span className="tw-text-smi tw-text-text-dark-medium-importance">
                              — {phone.fullname}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              !isDetailsLoading && (
                <div className="tw-mt-4 tw-text-smi tw-text-text-dark-medium-importance">
                  Adresse non disponible
                </div>
              )
            )}
          </div>

          {/* Colonne droite : Étape */}
          <div>
            <div className="modal-label-group">
              <div className="modal-label-title">Étape</div>
              <div className="modal-label-description">
                Informations de planification et consignes
              </div>
            </div>

            {isDetailsLoading ? (
              <div className="tw-mt-4 tw-space-y-3">
                <SkeletonLoader
                  width="100%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
                <SkeletonLoader
                  width="70%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
                <SkeletonLoader
                  width="55%"
                  height={14}
                  style={{ borderRadius: 4 }}
                />
              </div>
            ) : detailsData?.step ? (
              <div className="tw-mt-4 tw-space-y-3">
                {detailsData.step.estimated_delivery && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Livraison estimée
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {moment(detailsData.step.estimated_delivery).format(
                        'DD/MM/YYYY'
                      )}
                    </div>
                  </div>
                )}
                {detailsData.step.time_key && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Créneau
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {detailsData.step.time_key}
                    </div>
                  </div>
                )}
                {(detailsData.step.start_time || detailsData.step.end_time) && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Horaires
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {[detailsData.step.start_time, detailsData.step.end_time]
                        .filter(Boolean)
                        .join(' → ')}
                    </div>
                  </div>
                )}
                {detailsData.step.total_weight && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Poids total
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4">
                      {detailsData.step.total_weight}{' '}
                      {detailsData.step.weight_unit || ''}
                    </div>
                  </div>
                )}
                {detailsData.step.comments && (
                  <div>
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-leading-4 tw-mb-0.5">
                      Commentaires
                    </div>
                    <div className="tw-text-smi tw-font-work tw-leading-4 tw-whitespace-pre-wrap tw-p-2 tw-bg-slategray-default tw-rounded tw-border tw-border-solid tw-border-background">
                      {detailsData.step.comments}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              !isDetailsLoading && (
                <div className="tw-mt-4 tw-text-smi tw-text-text-dark-medium-importance">
                  Aucune information d'étape disponible
                </div>
              )
            )}
          </div>
        </div>

        {/* BLs et produits — pleine largeur */}
        {!isDetailsLoading && detailsData?.delivery_details?.length > 0 && (
          <div className="tw-mt-8 tw-border-t tw-pt-6">
            <div className="modal-label-group tw-mb-4">
              <div className="modal-label-title">
                Bons de livraison ({detailsData.delivery_details.length})
              </div>
              <div className="modal-label-description">
                Détail des BLs et des produits associés
              </div>
            </div>
            <div className="tw-space-y-3">
              {detailsData.delivery_details.map((bl, idx) => (
                <div
                  key={bl.id || bl.delivery_key || `bl-${idx}`}
                  className="tw-p-4 tw-bg-slategray-default tw-border tw-border-solid tw-border-background tw-rounded-lg"
                >
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <div className="tw-text-smi tw-font-medium tw-font-work">
                      BL #{bl.delivery_key || idx + 1}
                    </div>
                    {bl.delivery_date && (
                      <div className="tw-text-smi tw-text-text-dark-medium-importance">
                        {moment(bl.delivery_date).format('DD MMM YYYY')}
                      </div>
                    )}
                  </div>
                  {bl.items?.length > 0 ? (
                    <div>
                      <div className="tw-text-smi tw-text-text-dark-medium-importance tw-mb-2">
                        Produits ({bl.items.length})
                      </div>
                      <table className="table table-sm tw-mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Produit</th>
                            <th>Clé</th>
                            <th>Quantité</th>
                            <th>Poids</th>
                            <th>Mètres linéaires</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bl.items.map((item, iIdx) => (
                            <tr
                              key={item.id || item.item_key || `item-${iIdx}`}
                            >
                              <td className="tw-text-smi">
                                {item.name || '—'}
                              </td>
                              <td className="tw-text-smi tw-text-text-dark-medium-importance">
                                {item.item_key || '—'}
                              </td>
                              <td className="tw-text-smi">
                                {item.quantity ?? '—'}
                              </td>
                              <td className="tw-text-smi">
                                {item.weight != null
                                  ? `${item.weight} ${item.weight_unit || ''}`
                                  : '—'}
                              </td>
                              <td className="tw-text-smi">
                                {item.linear_meters != null
                                  ? `${item.linear_meters} m`
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="tw-text-smi tw-text-text-dark-medium-importance tw-italic">
                      Aucun produit
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </GenericModal>

      {/* Date Selection Modal */}
      <GenericModal
        isOpen={isDateModalOpen}
        setIsOpen={setIsDateModalOpen}
        title="Sélectionner la date de livraison"
        mainAction={handleConfirmScheduling}
        mainActionText="Confirmer"
        mainActionDisabled={!selectedDeliveryDate}
        mainActionIsLoading={isSchedulingLoading}
        secondaryAction={() => {
          setIsDateModalOpen(false);
          setSelectedDeliveryDate(null);
        }}
        secondaryActionText="Annuler"
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-items-center">
          <p className="tw-text-sm tw-text-gray-600 tw-text-center">
            Veuillez sélectionner la date à laquelle les{' '}
            <strong>{selectedItems.size}</strong> étape
            {selectedItems.size > 1 ? 's' : ''} sélectionnée
            {selectedItems.size > 1 ? 's' : ''} doivent être livrées.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center">
            <label className="tw-font-medium tw-text-sm">
              Date de livraison souhaitée
            </label>
            <ReactDatePicker
              selected={selectedDeliveryDate}
              onChange={(date) => setSelectedDeliveryDate(date)}
              locale="fr"
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              inline
            />
          </div>
          {selectedDeliveryDate && (
            <div className="tw-bg-blue-50 tw-border-2 tw-border-blue-200 tw-rounded-lg tw-p-4 tw-w-full">
              <p className="tw-text-sm tw-text-gray-700 tw-text-center">
                Date sélectionnée :{' '}
                <strong>
                  {moment(selectedDeliveryDate).format('dddd D MMMM YYYY')}
                </strong>
              </p>
            </div>
          )}
        </div>
      </GenericModal>
    </GenericPageContainer>
  );
}
